import React, { useEffect, useState } from 'react';
import { Modal } from '@/components/ui/Modal';
import { Button } from '@/components/ui/Button';
import { FileIcon } from '@/components/ui/FileIcon';
import type { DownloadModalProps } from '@/types/reports.types';

export const DownloadModal: React.FC<DownloadModalProps> = ({
  file,
  isOpen,
  onClose,
  onDownload,
}) => {
  const [isDownloading, setIsDownloading] = useState(false);
  const [downloadError, setDownloadError] = useState<string | null>(null);

  useEffect(() => {
    if (isOpen) {
      setDownloadError(null);
    }
  }, [isOpen, file?.fileId]);

  if (!file) return null;

  const handleModalClose = () => {
    if (!isDownloading) {
      setDownloadError(null);
      onClose();
    }
  };

  const handleDownload = async () => {
    setDownloadError(null);
    setIsDownloading(true);
    try {
      await onDownload(file);
      onClose();
    } catch (error) {
      setDownloadError(error instanceof Error ? error.message : 'Failed to download file');
    } finally {
      setIsDownloading(false);
    }
  };

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'long',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    });
  };

  return (
    <Modal
      isOpen={isOpen}
      onClose={handleModalClose}
      title="Download File"
      size="md"
      footer={
        <div className="flex w-full flex-col-reverse gap-2 pb-4 sm:w-auto sm:flex-row sm:gap-3 sm:pb-0">
          <Button variant="secondary" onClick={handleModalClose} disabled={isDownloading}>
            Cancel
          </Button>
          <Button
            variant="primary"
            onClick={handleDownload}
            disabled={isDownloading}
            loading={isDownloading}
            leftIcon={
              <svg className="h-4 w-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M12 10v6m0 0l-3-3m3 3l3-3m2 8H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z"
                />
              </svg>
            }
          >
            Download
          </Button>
        </div>
      }
    >
      <div className="space-y-6">
        {downloadError && (
          <div className="rounded-lg border border-red-200 bg-red-50 p-3 text-sm text-red-800 dark:border-red-800 dark:bg-red-900/20 dark:text-red-200">
            {downloadError}
          </div>
        )}

        {/* File Preview */}
        <div className="flex items-start gap-4">
          <div className="flex-shrink-0">
            <FileIcon fileName={file.fileName} size="xl" />
          </div>
          <div className="min-w-0 flex-1">
            <h3 className="font-semibold text-gray-900 dark:text-white">{file.fileCaption}</h3>
            <p className="mt-1 text-sm text-gray-500 dark:text-gray-400">{file.fileName}</p>
            <div className="mt-2 flex flex-wrap gap-4 text-xs text-gray-500 dark:text-gray-400">
              <span>Category: {file.fileCategory}</span>
              <span>Date: {formatDate(file.fileDate)}</span>
            </div>
          </div>
        </div>

        {/* File Details */}
        <div className="rounded-lg bg-gray-50 p-4 dark:bg-dark-900">
          <h4 className="text-sm font-medium text-gray-900 dark:text-white">File Information</h4>
          <dl className="mt-3 space-y-2">
            <div className="flex justify-between text-sm">
              <dt className="text-gray-500 dark:text-gray-400">File ID:</dt>
              <dd className="font-medium text-gray-900 dark:text-white">{file.fileId}</dd>
            </div>
            <div className="flex justify-between text-sm">
              <dt className="text-gray-500 dark:text-gray-400">Category:</dt>
              <dd className="font-medium text-gray-900 dark:text-white">{file.fileCategory}</dd>
            </div>
            <div className="flex justify-between text-sm">
              <dt className="text-gray-500 dark:text-gray-400">Created:</dt>
              <dd className="font-medium text-gray-900 dark:text-white">
                {formatDate(file.fileDate)}
              </dd>
            </div>
          </dl>
        </div>

        {/* Download Warning */}
        <div className="rounded-lg border border-amber-200 bg-amber-50 p-4 dark:border-amber-800 dark:bg-amber-900/20">
          <div className="flex">
            <div className="flex-shrink-0">
              <svg className="h-5 w-5 text-amber-400" fill="currentColor" viewBox="0 0 20 20">
                <path
                  fillRule="evenodd"
                  d="M8.257 3.099c.765-1.36 2.722-1.36 3.486 0l5.58 9.92c.75 1.334-.213 2.98-1.742 2.98H4.42c-1.53 0-2.493-1.646-1.743-2.98l5.58-9.92zM11 13a1 1 0 11-2 0 1 1 0 012 0zm-1-8a1 1 0 00-1 1v3a1 1 0 002 0V6a1 1 0 00-1-1z"
                  clipRule="evenodd"
                />
              </svg>
            </div>
            <div className="ml-3">
              <h3 className="text-sm font-medium text-amber-800 dark:text-amber-200">
                Download Notice
              </h3>
              <div className="mt-2 text-sm text-amber-700 dark:text-amber-300">
                <p>
                  By downloading this file, you acknowledge that you have the necessary permissions
                  to access this content. Please ensure you comply with your organization's data
                  handling policies.
                </p>
              </div>
            </div>
          </div>
        </div>
      </div>
    </Modal>
  );
};
